
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Loader2 } from "lucide-react";

interface Microsoft365AuthProps {
  onSuccess: (email: string, credentials: any) => void;
  accountName?: string;
}

const Microsoft365Auth = ({ onSuccess, accountName }: Microsoft365AuthProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Microsoft 365 OAuth flow
  const handleAuth = async () => {
    setIsAuthenticating(true);
    setError(null);

    try {
      console.log("Starting Microsoft 365 authentication flow");
      const redirectUri = window.location.origin + '/auth/microsoft-callback';
      console.log("Redirect URI:", redirectUri);
      
      // Get the OAuth URL from our edge function
      const { data, error } = await supabase.functions.invoke('microsoft365-auth', {
        body: {
          action: 'getAuthUrl',
          redirectUri: redirectUri,
          accountName: accountName || ''
        }
      });

      if (error) throw new Error(error.message);
      if (!data?.url) throw new Error('Failed to get Microsoft authentication URL');

      console.log("Generated auth URL:", data.url);
      
      // Store account name in session storage for retrieval after redirect
      if (accountName) {
        sessionStorage.setItem('ms365_account_name', accountName);
      }

      // Redirect to Microsoft login
      window.location.href = data.url;
    } catch (error: any) {
      console.error('Error starting Microsoft auth:', error);
      setError(error.message || "Failed to start Microsoft authentication");
      toast({
        title: "Authentication Failed",
        description: error.message || "Failed to start Microsoft authentication",
        variant: "destructive",
      });
      setIsAuthenticating(false);
    }
  };

  return (
    <div className="space-y-4">
      <Button 
        type="button" 
        onClick={handleAuth} 
        className="w-full"
        disabled={isAuthenticating}
      >
        {isAuthenticating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Connecting...
          </>
        ) : (
          "Connect Microsoft 365 Account"
        )}
      </Button>
      
      {error && (
        <div className="p-3 text-sm bg-red-50 border border-red-100 rounded text-red-600">
          Error: {error}
          <p className="mt-2 text-xs">Please make sure the Microsoft 365 app is properly configured.</p>
        </div>
      )}
    </div>
  );
};

export default Microsoft365Auth;
